import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { validateMetricRanges } from "@waterly/utils-isomorphic";
import type { MetricRangeType } from "src/libs/waterly-graphql-types/src";

import type { MetricRangeEditorProps } from "./MetricRangeEditor";
import MetricRangeEditor from "./MetricRangeEditor";
import type { MetricRangeCardMetricRange } from "./metricRangeUtils";
import { sanitizeMetricRange } from "./metricRangeUtils";
import RangeBar, { RangeBarMode } from "./RangeBar/RangeBar";

export type MetricRangesCardProps = {
  /** Optional frontend action rendered only during setup; use onChange to replace the draft ranges. */
  cloneMetricRangesComponent?: MetricRangeEditorProps["cloneMetricRangesComponent"];
  metricRangeType: MetricRangeType | null;
  metricRanges: MetricRangeCardMetricRange[];
  onChange: MetricRangeEditorProps["onChange"];
  devMode: boolean;
  metricRangeTagOptions: MetricRangeEditorProps["metricRangeTagOptions"];
  onDraftErrorChange?: (hasDraftError: boolean) => void;
};

const MetricRangesCard = ({
  metricRanges,
  metricRangeType,
  onChange,
  cloneMetricRangesComponent,
  devMode,
  metricRangeTagOptions,
  onDraftErrorChange,
}: MetricRangesCardProps) => {
  const { t } = useTranslation(["common", "errors"]);
  const theme = useTheme();

  const activeMetricRanges = useMemo(() => metricRanges.filter((mr) => !mr.deletedAt), [metricRanges]);
  const [hasEditorDraftError, setHasEditorDraftError] = useState(false);

  const { error: activeError } = useMemo<ReturnType<typeof validateMetricRanges>>(() => {
    return activeMetricRanges.length > 0
      ? validateMetricRanges(activeMetricRanges.map((r) => sanitizeMetricRange(r)))
      : { error: null, success: true };
  }, [activeMetricRanges]);

  const hasActiveValidationError = Boolean(activeError && activeMetricRanges.length > 1);
  const hasDraftError = hasEditorDraftError || hasActiveValidationError;

  // Keep the parent in sync with the card-level draft state, including editor and validation errors.
  useEffect(() => {
    onDraftErrorChange?.(hasDraftError);
  }, [hasDraftError, onDraftErrorChange]);

  // Clear the parent's draft-error flag when this card unmounts so stale errors do not block saves.
  useEffect(() => {
    return () => onDraftErrorChange?.(false);
  }, [onDraftErrorChange]);

  const debug = useMemo(() => {
    const renderDebug = ({ title, value }: { title: string; value: unknown }) => {
      return (
        <details>
          <summary>{title}</summary>
          <pre>{JSON.stringify(value, null, 2)}</pre>
        </details>
      );
    };

    if (!devMode) return null;

    return (
      <Box mt={4}>
        {`metricRangeType: ${metricRangeType}`}
        {renderDebug({ title: "All MetricRanges", value: metricRanges })}
        {renderDebug({ title: "New MetricRanges", value: metricRanges.filter((mr) => mr.isNew) })}
        {renderDebug({
          title: "Existing MetricRanges",
          value: metricRanges.filter((mr) => !mr.isNew),
        })}
        {renderDebug({
          title: "Deleted MetricRanges",
          value: metricRanges.filter((mr) => mr.deletedAt),
        })}
      </Box>
    );
  }, [devMode, metricRangeType, metricRanges]);

  return (
    <Box
      sx={{
        border: `1px solid`,
        borderColor: activeError ? theme.palette.error.main : undefined,
        borderRadius: 1.5,
        p: 1.5,
        mt: 3,
      }}
    >
      <Stack display="flex" mb={2.5}>
        <Typography mr={3} variant="h4">
          {t("common:ranges")}
        </Typography>
        {activeMetricRanges.length > 0 && (
          <RangeBar
            mode={RangeBarMode.EDIT}
            allRanges={activeMetricRanges}
            currentRange={sanitizeMetricRange(activeMetricRanges[0])}
          />
        )}
      </Stack>
      <MetricRangeEditor
        cloneMetricRangesComponent={cloneMetricRangesComponent}
        activeMetricRanges={activeMetricRanges}
        metricRanges={metricRanges}
        metricRangeType={metricRangeType}
        onChange={onChange}
        metricRangeTagOptions={metricRangeTagOptions}
        devMode={devMode}
        onDraftErrorChange={setHasEditorDraftError}
      />
      {hasActiveValidationError && activeError && (
        <Box display="flex" flexDirection="column" gap={0.75}>
          <Typography color="error">{t(`errors:${activeError.code}`)}</Typography>
          {devMode && <Typography color="error">{activeError.message}</Typography>}
        </Box>
      )}
      {debug}
    </Box>
  );
};

export default MetricRangesCard;
